import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
  useCallback,
} from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';

import type { Language, Direction, Translations } from './types';
import { ar } from './translations/ar';
import { en } from './translations/en';

const translations: Record<Language, Translations> = { ar, en };

interface I18nContextValue {
  language: Language;
  direction: Direction;
  t: Translations;
  switchLanguage: () => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const languageConfig: Record<Language, { direction: Direction }> = {
  ar: { direction: 'rtl' },
  en: { direction: 'ltr' },
};

export function I18nProvider({ children }: { children: ReactNode }) {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const language: Language = lang === 'en' ? 'en' : 'ar';
  const direction = languageConfig[language].direction;
  const t = translations[language];

  const switchLanguage = useCallback(() => {
    const newLang = language === 'ar' ? 'en' : 'ar';
    const newPath = location.pathname.replace(`/${language}`, `/${newLang}`);
    navigate(newPath + location.search + location.hash);
  }, [language, location.pathname, location.search, location.hash, navigate]);

  const value = useMemo(
    () => ({ language, direction, t, switchLanguage }),
    [language, direction, t, switchLanguage],
  );

  return (
    <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}
